import { snakeCase } from 'lodash';
import { assign, createMachine } from 'xstate';
import { isPowerSetter } from '~/funnels/powersetter/machine';
import { contextCreator } from '~/machines/utils/context';
import { LDClient, LDFlags } from '~/providers';
import { Document } from '~/screens/lmi-proof-docs-upload/component/document-details.component';
import { LMIProofDoc } from '~/screens/lmi-proof-docs-upload/component/lmi-proof-docs-upload.component';
import { uploadLMIProofDoc } from '~/services';
import { LmiProofDocUploadDocumentType, LmiProofDocUploadRelationship } from '~/typings/graphql';
import { sendToSegment } from '~/utils/tracking';
import { isEnergyWell } from '../solar-funnel';
import { UploadContext, UploadEvent, UploadServices } from './types';

export const uploadMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as UploadContext,
        events: {} as UploadEvent,
        services: {} as UploadServices,
      },
      context: {
        funnelPacket: {} as UploadContext['funnelPacket'],
        documentTypes: [
          'Comfort Partners',
          'Lifeline USAC',
          'Lifeline Utility Assistance Program',
          'LIHEAP',
          'Medicaid',
          'PAGE',
          'Qualifying Income',
          'Section 8',
          'SNAP',
          'Universal Service Fund',
        ],
        docs: [],
      },
      initial: 'idle',
      entry: ['trackStarted', 'setShouldEnableSkip'],
      states: {
        idle: {
          on: {
            SUBMIT_DOCS: { target: 'submitting' },
            SKIP: 'completed',
          },
        },
        submitting: {
          invoke: {
            src: 'saveFiles',
            onDone: { target: 'completed', actions: 'updateSavedFiles' },
            onError: { target: 'idle' },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: 'trackCompleted',
        },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        trackStarted: context => {
          sendToSegment({
            type: 'upload_lmi_proof_docs_step_started',
            funnelPacket: context.funnelPacket,
          });
        },
        trackCompleted: context => {
          sendToSegment({
            type: 'upload_lmi_proof_docs_step_completed',
            funnelPacket: context.funnelPacket,
            documentTypes: context.docs.map(doc => doc.documentType),
          });
        },
        setShouldEnableSkip: assign((context, _event) => {
          if (isEnergyWell(context) || isPowerSetter(context)) {
            return {
              shouldEnableSkip: true,
            };
          }
          return {};
        }),
        updateSavedFiles: assign((_context, event) => {
          const uploadedDocs = event.data;
          return {
            docs: uploadedDocs,
          };
        }),
      },
      guards: {},
      services: {
        saveFiles: async (context, event) => {
          const utilityAccountId = context.funnelPacket.utilityAccount.id;
          const { docs } = event.data;
          let results: LMIProofDoc[] = [];
          for (const doc of docs) {
            let result: LMIProofDoc | undefined = {} as LMIProofDoc;
            try {
              if (!LDClient.variation(LDFlags.enableManualProofCapture)) {
                const { documentType, image } = doc as LMIProofDoc;
                result = await uploadLMIProofDoc({
                  utilityAccountId,
                  documentType,
                  file: image,
                  base64Image: image.base64Image!,
                });
              } else {
                const { documentType, files, relationship, name } = doc as Document;
                for (const eachFile of files) {
                  const { proofType, file } = eachFile;
                  result = await uploadLMIProofDoc({
                    utilityAccountId,
                    documentType,
                    file,
                    base64Image: file.base64Image!,
                    name,
                    proofType: snakeCase(proofType) as LmiProofDocUploadDocumentType,
                    relationship: relationship.split('-')[0] as LmiProofDocUploadRelationship,
                  });
                }
              }
              if (result.image.error) {
                throw new Error(result.image.error);
              }
              results.push(result);
            } catch (error) {
              results = [];
              throw new Error(error);
            }
          }

          return results;
        },
      },
    },
  );

export const uploadContext = contextCreator<UploadContext, 'funnelPacket'>(uploadMachine);
